import { RootState, store } from '@store/store'
import { DjangoFilter } from '@store/slices/generic.slice'
import {
  AISlotModel,
  fetchAllAiSlots,
  setAiSlotInProgress,
} from '@store/slices/aiSlot.slice'
import { PlaylistModel } from '@store/slices/playlist.slice'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import { TaskModel } from '@store/slices/task.slice'
import { poolNewTask } from '@store/actions/task.actions'
import {
  generateSlotOutline,
  narrateSlot,
  publishAllSlot,
  publishUpdatedSlot,
  transcribeSlot,
} from '@store/thunk/aiSlot.thunk'
import {
  NarrationModel,
  selectNarrationsByIds,
  setNarrationInProgress,
} from '@store/slices/narration.slice'
import { setAiMetadataInProgress } from '@store/slices/aiMeta.slice'

export const handlePublishUpdatedForSlot = async (
  slot: AISlotModel,
  playlistId: EntityId
): Promise<EntityId> => {
  const state: RootState = store.getState()
  const dispatch = store.dispatch
  const slotNarrations: NarrationModel[] = selectNarrationsByIds(
    state,
    slot.narrations
  )
  const affectedNarrations: NarrationModel[] = slotNarrations.filter(
    (narration: NarrationModel) =>
      narration.audioCreated &&
      narration.uploaded &&
      new Date(narration.audioCreated) > new Date(narration.uploaded)
  )
  const affectedNarrationsIds: EntityId[] = affectedNarrations.map(
    (narration) => narration.id
  )

  return dispatch(publishUpdatedSlot(slot.id))
    .then(unwrapResult)
    .then((taskId: EntityId) => {
      const newTask: TaskModel = {
        id: taskId,
        title: `Publikace aktualizovaných audií pro slot ${slot.id}`,
        relatedLink: `/playlist/${playlistId}/slot/${slot.id}`,
        relatedObjects: [
          {
            entityName: 'narration',
            entityIds: [...affectedNarrationsIds],
          },
          {
            entityName: 'aiSlot',
            entityIds: [slot.id],
          },
          {
            entityName: 'playlist',
            entityIds: [...slot.playlists],
          },
        ],
      }
      dispatch(poolNewTask(newTask))
      dispatch(setNarrationInProgress(affectedNarrationsIds))
      dispatch(setAiSlotInProgress(slot.id))
      return taskId
    })
}

export const handlePublishAllForSlot = async (
  slot: AISlotModel,
  playlistId: EntityId
): Promise<EntityId> => {
  const state: RootState = store.getState()
  const dispatch = store.dispatch
  const slotNarrations: NarrationModel[] = selectNarrationsByIds(
    state,
    slot.narrations
  )
  const affectedNarrations: NarrationModel[] = slotNarrations.filter(
    (narration: NarrationModel) =>
      narration.audioCreated &&
      (!narration.uploaded ||
        new Date(narration.audioCreated) > new Date(narration.uploaded))
  )
  const affectedNarrationsIds: EntityId[] = affectedNarrations.map(
    (narration) => narration.id
  )
  return dispatch(publishAllSlot(slot.id))
    .then(unwrapResult)
    .then((taskId: EntityId) => {
      const newTask: TaskModel = {
        id: taskId,
        title: `Publikace audií slotu ${slot.id}`,
        relatedLink: `/playlist/${playlistId}/slot/${slot.id}`,
        relatedObjects: [
          {
            entityName: 'narration',
            entityIds: [...affectedNarrationsIds],
          },
          {
            entityName: 'aiSlot',
            entityIds: [slot.id],
          },
          {
            entityName: 'playlist',
            entityIds: [...slot.playlists],
          },
        ],
      }
      dispatch(poolNewTask(newTask))
      dispatch(setNarrationInProgress(affectedNarrationsIds))
      dispatch(setAiSlotInProgress(slot.id))
      return taskId
    })
}

export const handleNarrateSlot = async (
  slot: AISlotModel,
  playlistId: EntityId
): Promise<EntityId> => {
  const dispatch = store.dispatch
  return dispatch(narrateSlot({ slotId: slot.id, playlistId: playlistId }))
    .then(unwrapResult)
    .then((taskId: EntityId) => {
      const newTask: TaskModel = {
        id: taskId,
        title: `Narace slotu ${slot.id}`,
        relatedLink: `/playlist/${playlistId}/slot/${slot.id}`,
        relatedObjects: [
          {
            entityName: 'narration',
            entityIds: [...slot.narrations],
          },
          {
            entityName: 'aiSlot',
            entityIds: [slot.id],
          },
          {
            entityName: 'playlist',
            entityIds: [playlistId],
          },
        ],
      }
      dispatch(poolNewTask(newTask))
      dispatch(setNarrationInProgress(slot.narrations))
      return taskId
    })
}

export const handleTranscribeSlot = async (
  slot: AISlotModel,
  playlistId: EntityId
): Promise<EntityId> => {
  const dispatch = store.dispatch
  return dispatch(transcribeSlot({ slotId: slot.id, playlistId: playlistId }))
    .then(unwrapResult)
    .then((taskId: EntityId) => {
      const newTask: TaskModel = {
        id: taskId,
        title: `Tvorba dialogu slotu ${slot.id}`,
        relatedLink: `/playlist/${playlistId}/slot/${slot.id}`,
        relatedObjects: [
          {
            entityName: 'narration',
            entityIds: [...slot.narrations],
          },
          {
            entityName: 'aiSlot',
            entityIds: [slot.id],
          },
          {
            entityName: 'playlist',
            entityIds: [playlistId],
          },
        ],
      }
      dispatch(poolNewTask(newTask))
      dispatch(setNarrationInProgress(slot.narrations))
      return taskId
    })
}

export const handleGenerateSlotOutline = async (
  slot: AISlotModel,
  playlistId: EntityId
): Promise<EntityId> => {
  const dispatch = store.dispatch
  return dispatch(
    generateSlotOutline({ slotId: slot.id, playlistId: playlistId })
  )
    .then(unwrapResult)
    .then((taskId: EntityId) => {
      const newTask: TaskModel = {
        id: taskId,
        title: `Generování návrhu pro slot ${slot.id}`,
        relatedLink: `/playlist/${playlistId}/slot/${slot.id}`,
        relatedObjects: [
          {
            entityName: 'aiMetadata',
            entityIds: slot.aiEntryMetadata ? [slot.aiEntryMetadata] : [],
          },
          {
            entityName: 'aiSlot',
            entityIds: [slot.id],
          },
          {
            entityName: 'playlist',
            entityIds: [playlistId],
          },
        ],
      }
      dispatch(poolNewTask(newTask))
      slot.aiEntryMetadata &&
        dispatch(setAiMetadataInProgress(slot.aiEntryMetadata))
      return taskId
    })
}

export const fetchSlotsForPlaylist = async (
  playlist: PlaylistModel
): Promise<AISlotModel[]> => {
  const dispatch = store.dispatch
  const filter = new DjangoFilter<AISlotModel>()
  filter.addIdFilter(playlist.aiSlots)
  return dispatch(fetchAllAiSlots(filter)).then(unwrapResult)
}
